import { useEffect, useRef, useState, type ForwardedRef, type MutableRefObject } from 'react';

import debounce from '../utils/debounce';

export const useIsOverflown = (ref?: ForwardedRef<HTMLElement>, disabled?: boolean) => {
    const [isOverflown, setIsOverflown] = useState(false);
    const defaultRef = useRef<HTMLElement | null>();
    const nodeRef = (ref || defaultRef) as MutableRefObject<HTMLElement | null>;
    const unobserveRef = useRef<(() => void) | undefined>(undefined);

    const startObserver = (node: HTMLElement) => {
        const handler = () => {
            setIsOverflown(node.scrollWidth > node.clientWidth);
        };
        handler();
        const debouncedHandler = debounce(handler, 500);
        const resizeObserver = new ResizeObserver(() => debouncedHandler());
        resizeObserver.observe(node);
        unobserveRef.current = () => resizeObserver.disconnect();
    };

    const refFn = (node: HTMLElement) => {
        if (nodeRef.current === node) {
            return;
        }
        unobserveRef.current?.();
        nodeRef.current = node;
        if (node && !disabled) {
            startObserver(node);
        }
    };

    useEffect(() => {
        return () => {
            unobserveRef.current?.();
        };
    }, []);

    return [refFn, isOverflown] as const;
};
